/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { ListItem } from '@app/data-model/item/list-item';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';

export class WorkItem extends ListItem {
  id: number;
  imageCount: number;
  rejectedImageCount: number;
  accession: string;
  priority: string;
  priorityId: number;
  procedureCode: string;
  procedureText: string;
  status: string;
  statusId: number;
  managingOrganizationId: number;
  instituteName: string;
  managingOrganizationType: string;
  orderingLocation: string;
  patientDoB: string;
  timeOfExam: string;
  modality: string;
  bodyPart: string;
  bodyRegion: string;
  indication: string;
  studyDataAvailability: string;
  availability: string;
  patientName: any;
  patientVipCode: string;
  reasonForExam: string;
  state: any;
  examSaved: boolean;
  examNoteCount: number;
  priorityColor: string;
  patientAge: string;

  referringPhysician;
  responsibleAuthor;
  nextApproverId;
  designatedAuthor;
  initialAuthor;
  lastAuthor;
  patientIdentifier;
  decorators;
  mrn;
  nhi;
  archiveAdoptedStatus: string;
  examTagDetails;

  requestedProcedureId: number;
  protocolWorklist;
  patientId;
  patientGender;
  protocolStat;
  repositoryType;
  repositoryName;
  repositoryId;
  currentLocation;
  procedureImprText;
  aiFinding;
  aiStatus;
  setContextByGenEx: boolean;
  setContextByExternalApp: boolean;
  isOpenedFromUrl: boolean;
  viewerIdentifier;
  associationList;
  departmentName;
  workItem?: any;

  [key: string]: any;

  private extractedConfigurableObjectMappings(task) {
    for (let k = 0; k < appConstants.configurableObjectMappings.length; k++) {
      const __ret = this.extractedItemConfig(k, task);
    }
  }

  private extractedItemConfig(k, task) {
    const configMapping = appConstants.configurableObjectMappings[k];
    const configKey = _(configMapping).chain().keys().first().value();
    const configfrom = configMapping.workItem ? task.workItem : task;
    const configvalue = configfrom !== undefined ? configfrom[configMapping[configKey]] : undefined;
    if (_.isNumber(configvalue) || !_.isEmpty(configvalue) || _.isBoolean(configvalue)) {
      this[configKey] = configvalue;
    }
    return {
      configMapping: configMapping,
      configKey: configKey,
      configfrom: configfrom,
      configvalue: configvalue
    };
  }

  toTaskDto() {
    const task = { workItem: {} };
    for (let i = 0; i < appConstants.objectMappings.length; i++) {
      let mapping = appConstants.objectMappings[i];
      const to = mapping.workItem ? task.workItem : task;
      mapping = _.invert(mapping);
      const key = _(mapping).chain().keys().first().value();
      to[key] = this[mapping[key]];
    }
    return task;
  }

  constructor(id: any, params?: any) {
    super('work-item');
    let task;
    if (arguments.length === 1 && typeof id === 'object') {
      task = id;
    }

    if (task) {
      // Global Exam
      this.processGlobalExam(task);
      // Remote Exam
      this.processRemoteExams(task);
      this.processMappings(task);
    } else {
      this.processParams(params, id);
    }
    this.bodyPart = this.bodyPart || this.procedureText;
  }

  private processGlobalExam(task) {
    // if the task item is global task item, then modify  requestedProcedureId to repositoryId*requestedProcedureId
    if (UtilService.isGlobalExam(task.workItem)) {
      task.workItem.requestedProcedureId = task.workItem.repositoryId + '*' + task.workItem.requestedProcedureId;
    }
  }
  private processParams(params: any, id: any) {
    this.reasonForExam = params.orderReason;
    this.id = id;
    this.accession = params.accession;
    this.priority = params.priority;
    this.priorityId = params.priorityId;
    this.priorityColor = params.priorityColor;
    this.procedureCode = params.procedureCode;
    this.procedureText = params.procedureText;
    this.status = status;
    this.statusId = params.statusId;
    this.managingOrganizationId = params.managingOrganizationId;
    this.managingOrganizationType = params.managingOrganizationType;
    this.orderingLocation = params.orderingLocation;
    this.timeOfExam = params.examDate;
    this.modality = params.modality;
    this.bodyPart = params.bodyPart;
    this.bodyRegion = params.bodyPart;
    this.indication = params.indication;
    this.patientDoB = params.patientBirthDate;
    this.referringPhysician = params.referringPhysician;
    this.studyDataAvailability = params.studyDataAvailability;
    this.patientName = params.patientName;
    this.patientIdentifier = params.patientIdentifier;
    this.protocolStat = params.protocolStat;
    this.archiveAdoptedStatus = params.archiveAdoptedStatus;
    this.examTagDetails = params.examTagDetails;
    this.designatedAuthor = params.designatedAuthor;
    this.nextApproverId = params.nextApproverId;
    this.isOpenedFromUrl = params.isOpenedFromUrl;
  }

  private processMappings(task) {
    const mappings =
      task.workItem && task.workItem.source && task.workItem.source === 'patient-history'
        ? appConstants.comparisonObjectMappings
        : appConstants.objectMappings;
    for (let i = 0; i < mappings.length; i++) {
      const mapping = mappings[i];
      const key = _(mapping).chain().keys().first().value();
      const from = mapping.workItem ? task.workItem : task;
      this[key] = from !== undefined ? from[mapping[key]] : undefined;
    }
    // departmentName only applies for quick search results
    if (task && task.workItem) {
      this.departmentName = task.workItem.departmentName;
    }
    this.source = task.workItem.source;
    this.extractedConfigurableObjectMappings(task);
  }

  private processRemoteExams(task) {
    if (task && UtilService.isRemoteExam(task.workItem)) {
      // procedureStudyInstanceUID is assigned to requestedProcedureId for remote exam
      task.workItem.requestedProcedureId = task.workItem.procedureStudyInstanceUID;
      // Remote exams does not have pat_ckey as patientId so patientIdentifier (MRN) which can only be in single
      // domain is assigned to patientId so that it can be used in patient matching logic when exams are
      // compared
      task.workItem.patientId = task.workItem.patientIdentifier[0].value;
    }
  }
}
